import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useBadgeRefresh } from "../../contexts/BadgeRefreshContext";
import { canApproveLeave, canManageLeaveCredits } from "../../services/api";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ApprovalIcon from "@mui/icons-material/Approval";
import PersonIcon from "@mui/icons-material/Person";
import ListAltIcon from "@mui/icons-material/ListAlt";
import leaveApiService from "../../services/leave-api";

// Import components
import EmployeeDashboard from "./components/EmployeeDashboard";
import PendingApprovals from "./components/PendingApprovals";
import EmployeeLeaveCredits from "./components/EmployeeLeaveCredits";
import LeaveList from "./components/LeaveList";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leave-tabpanel-${index}`}
      aria-labelledby={`leave-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `leave-tab-${index}`,
    "aria-controls": `leave-tabpanel-${index}`,
  };
}

const LeavePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { refreshTrigger } = useBadgeRefresh();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tabValue, setTabValue] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [pendingLeaveCount, setPendingLeaveCount] = useState<number>(0);

  // Check user permissions
  const canApprove = canApproveLeave(user || null);
  const canManageCredits = canManageLeaveCredits(user || null);

  // Define available tabs based on user permissions
  const tabs = [
    {
      label: t("leave.dashboard"),
      icon: <DashboardIcon />,
      component: <EmployeeDashboard />,
      show: true,
      showInMobile: true, // Show in mobile for all users
    },
    {
      label: t("leave.pendingApprovals"),
      icon:
        pendingLeaveCount > 0 ? (
          <Badge badgeContent={pendingLeaveCount} color="error" max={99}>
            <ApprovalIcon />
          </Badge>
        ) : (
          <ApprovalIcon />
        ),
      component: <PendingApprovals />,
      show: canApprove, // Approvers and Admins only
      showInMobile: canApprove, // Show in mobile only for Admin/Approvers
    },
    {
      label: t("leave.employeeLeaveCredits"),
      icon: <PersonIcon />,
      component: <EmployeeLeaveCredits />,
      show: canManageCredits, // Admin only
      showInMobile: false, // Hide in mobile (even for Admin)
    },
    {
      label: t("leave.leaveList"),
      icon: <ListAltIcon />,
      component: <LeaveList />,
      show: canManageCredits, // Admin only
      showInMobile: false, // Hide in mobile (even for Admin)
    },
  ];

  // Filter tabs based on permissions and mobile view
  const visibleTabs = tabs.filter((tab) => {
    if (isMobile) {
      return tab.show && tab.showInMobile;
    }
    return tab.show;
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  // Fetch pending leave requests count for approvers
  useEffect(() => {
    if (!canApprove) {
      return;
    }

    const fetchPendingLeaveCount = async () => {
      try {
        const count = await leaveApiService.getPendingRequestsCount();
        setPendingLeaveCount(count);
      } catch (error) {
        console.error("Failed to fetch pending leave count:", error);
      }
    };

    // Fetch immediately
    fetchPendingLeaveCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingLeaveCount, 30000);

    return () => clearInterval(interval);
  }, [canApprove, refreshTrigger]);

  useEffect(() => {
    // Reset to first tab if current tab becomes unavailable
    if (tabValue >= visibleTabs.length) {
      setTabValue(0);
    }
  }, [tabValue, visibleTabs.length]);

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">{t("leave.messages.noPermission")}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">{t("leave.title")}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                minHeight: 64,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition={isMobile ? "top" : "start"}
                label={tab.label}
                {...a11yProps(index)}
                sx={{
                  minWidth: isMobile ? 120 : 160,
                  "& .MuiTab-iconWrapper": {
                    marginBottom: isMobile ? 0.5 : 0,
                    marginRight: isMobile ? 0 : 1,
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {visibleTabs.map((tab, index) => (
          <TabPanel key={index} value={tabValue} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Paper>
    </Container>
  );
};

export default LeavePage;
